/**
 * Copia de la BD fuera del servidor: tipos y lógica pura del formulario (sin React, para poder probarla).
 */
export type Destination = "NONE" | "SFTP" | "S3" | "EMAIL";

export interface OffsiteView {
  configured: boolean;
  encryptionKeyConfigured: boolean;
  minKeep: number;
  enabled: boolean;
  runHour: number;
  runMinute: number;
  retentionDays: number;
  destination: Destination;
  encryptEnabled: boolean;
  encryptKeyFingerprint: string | null;
  sftp: {
    host: string;
    port: number;
    username: string;
    directory: string;
    hasPassword: boolean;
    hasPrivateKey: boolean;
    hostFingerprint: string | null;
  } | null;
  s3: {
    endpoint: string | null;
    region: string;
    bucket: string;
    prefix: string;
    accessKeyId: string;
    hasSecret: boolean;
  } | null;
  emailTo: string | null;
  last: {
    attemptAt: string | null;
    successAt: string | null;
    status: string | null;
    error: string | null;
    fileName: string | null;
    sizeBytes: number | null;
    retentionNote: string | null;
    consecutiveFailures: number;
  } | null;
}

export interface OffsiteForm {
  enabled: boolean;
  time: string; // HH:MM
  retentionDays: string;
  destination: Destination;
  encryptEnabled: boolean;
  encryptPublicKey: string;
  sftpHost: string;
  sftpPort: string;
  sftpUsername: string;
  sftpPassword: string;
  sftpPrivateKey: string;
  sftpDirectory: string;
  sftpFingerprint: string;
  sftpFingerprintConfirmed: boolean;
  s3Endpoint: string;
  s3Region: string;
  s3Bucket: string;
  s3Prefix: string;
  s3AccessKeyId: string;
  s3SecretAccessKey: string;
  emailTo: string;
}

const pad = (n: number): string => String(n).padStart(2, "0");

export function formFromView(v: OffsiteView): OffsiteForm {
  return {
    enabled: v.enabled,
    time: `${pad(v.runHour)}:${pad(v.runMinute)}`,
    retentionDays: String(v.retentionDays),
    destination: v.destination,
    encryptEnabled: v.encryptEnabled,
    encryptPublicKey: "",
    sftpHost: v.sftp?.host ?? "",
    sftpPort: String(v.sftp?.port ?? 22),
    sftpUsername: v.sftp?.username ?? "",
    sftpPassword: "",
    sftpPrivateKey: "",
    sftpDirectory: v.sftp?.directory ?? "deca-backups",
    sftpFingerprint: v.sftp?.hostFingerprint ?? "",
    // La huella ya guardada se dio por confirmada en su momento; si el servidor cambia hay que confirmar otra.
    sftpFingerprintConfirmed: Boolean(v.sftp?.hostFingerprint),
    s3Endpoint: v.s3?.endpoint ?? "",
    s3Region: v.s3?.region ?? "auto",
    s3Bucket: v.s3?.bucket ?? "",
    s3Prefix: v.s3?.prefix ?? "deca-backups",
    s3AccessKeyId: v.s3?.accessKeyId ?? "",
    s3SecretAccessKey: "",
    emailTo: v.emailTo ?? "",
  };
}

export const EMPTY_VIEW: OffsiteView = {
  configured: false,
  encryptionKeyConfigured: true,
  minKeep: 3,
  enabled: false,
  runHour: 3,
  runMinute: 40,
  retentionDays: 14,
  destination: "NONE",
  encryptEnabled: false,
  encryptKeyFingerprint: null,
  sftp: null,
  s3: null,
  emailTo: null,
  last: null,
};

export function parseTime(time: string): { runHour: number; runMinute: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!m) return null;
  const runHour = Number(m[1]);
  const runMinute = Number(m[2]);
  if (runHour > 23 || runMinute > 59) return null;
  return { runHour, runMinute };
}

/** Errores que se pueden detectar sin llamar al servidor. */
export function validateForm(f: OffsiteForm, view: OffsiteView): string | null {
  if (!parseTime(f.time)) return "La hora no es válida (formato HH:MM).";
  const days = Number(f.retentionDays);
  if (!Number.isInteger(days) || days < 3 || days > 3650) return "Los días de retención deben estar entre 3 y 3650.";
  if (f.enabled && f.destination === "NONE") return "Elige un destino antes de activar la copia automática.";
  if (f.encryptEnabled && !f.encryptPublicKey.trim() && !view.encryptKeyFingerprint) {
    return "Pega la clave pública para cifrar las copias.";
  }
  if (f.destination === "EMAIL") {
    if (!f.encryptEnabled) return "Por correo la copia debe ir cifrada: activa el cifrado.";
    if (!f.emailTo.trim()) return "Indica al menos un correo de destino.";
  }
  if (f.destination === "SFTP") {
    if (!f.sftpHost.trim() || !f.sftpUsername.trim() || !f.sftpDirectory.trim()) return "Completa servidor, usuario y carpeta del SFTP.";
    const port = Number(f.sftpPort);
    if (!Number.isInteger(port) || port < 1 || port > 65535) return "El puerto SFTP no es válido.";
    if (!f.sftpFingerprint.startsWith("SHA256:")) return "Pulsa «Leer huella» y confírmala antes de guardar.";
    if (!f.sftpFingerprintConfirmed) return "Confirma que la huella del servidor es la correcta.";
    const sameServer =
      view.sftp?.host === f.sftpHost.trim().toLowerCase() && view.sftp?.username === f.sftpUsername.trim();
    const hasSecret = f.sftpPassword || f.sftpPrivateKey.trim() || (sameServer && (view.sftp?.hasPassword || view.sftp?.hasPrivateKey));
    if (!hasSecret) return "Indica la contraseña o la clave privada del usuario SFTP.";
  }
  if (f.destination === "S3") {
    if (!f.s3Bucket.trim() || !f.s3AccessKeyId.trim()) return "Completa bucket y clave de acceso.";
    if (f.s3Endpoint.trim() && !/^https:\/\//i.test(f.s3Endpoint.trim())) return "El endpoint debe empezar por https://";
    const sameBucket = view.s3?.bucket === f.s3Bucket.trim() && view.s3?.accessKeyId === f.s3AccessKeyId.trim();
    if (!f.s3SecretAccessKey && !(sameBucket && view.s3?.hasSecret)) return "Indica la clave secreta de acceso.";
  }
  return null;
}

/** Cuerpo del PUT. Los secretos vacíos se omiten (el servidor conserva los guardados). */
export function buildPayload(f: OffsiteForm): Record<string, unknown> {
  const t = parseTime(f.time)!;
  const body: Record<string, unknown> = {
    enabled: f.enabled,
    runHour: t.runHour,
    runMinute: t.runMinute,
    retentionDays: Number(f.retentionDays),
    destination: f.destination,
    encryptEnabled: f.encryptEnabled,
  };
  if (f.encryptPublicKey.trim()) body.encryptPublicKey = f.encryptPublicKey.trim();
  if (f.destination === "SFTP") {
    body.sftp = {
      host: f.sftpHost.trim(),
      port: Number(f.sftpPort),
      username: f.sftpUsername.trim(),
      directory: f.sftpDirectory.trim(),
      hostFingerprint: f.sftpFingerprint,
      ...(f.sftpPassword ? { password: f.sftpPassword } : {}),
      ...(f.sftpPrivateKey.trim() ? { privateKey: f.sftpPrivateKey.trim() } : {}),
    };
  }
  if (f.destination === "S3") {
    body.s3 = {
      ...(f.s3Endpoint.trim() ? { endpoint: f.s3Endpoint.trim() } : {}),
      region: f.s3Region.trim() || "auto",
      bucket: f.s3Bucket.trim(),
      prefix: f.s3Prefix.trim(),
      accessKeyId: f.s3AccessKeyId.trim(),
      ...(f.s3SecretAccessKey ? { secretAccessKey: f.s3SecretAccessKey } : {}),
    };
  }
  if (f.destination === "EMAIL") body.emailTo = f.emailTo.trim();
  return body;
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export type LastTone = "ok" | "warn" | "danger" | "neutral";

/** Estado visible de la última copia. Si hace más de 26 h de la última correcta con la copia activa, avisa. */
export function lastStatus(v: OffsiteView, now: Date = new Date()): { tone: LastTone; label: string } {
  if (!v.last) return { tone: "neutral", label: v.enabled ? "Aún no se ha hecho ninguna copia" : "Sin copias" };
  if (v.last.status === "RUNNING") return { tone: "warn", label: "Copia en curso…" };
  if (v.last.status === "FAILED") return { tone: "danger", label: "La última copia falló" };
  if (v.enabled && v.last.successAt && now.getTime() - new Date(v.last.successAt).getTime() > 26 * 3600_000) {
    return { tone: "warn", label: "Hace más de 26 h de la última copia correcta" };
  }
  return { tone: "ok", label: "Última copia correcta" };
}

export interface RemoteBackupFile {
  name: string;
  createdAt: string;
}

/**
 * Aviso previo al borrado: si entre lo elegido está la copia más reciente o se van a borrar todas, el
 * super-admin debe saberlo antes de confirmar. `null` si no hay nada especial que avisar.
 */
export function deletionWarning(files: RemoteBackupFile[], selected: ReadonlySet<string>): string | null {
  const chosen = files.filter((f) => selected.has(f.name));
  if (chosen.length === 0) return null;
  if (chosen.length === files.length) {
    return "Vas a borrar TODAS las copias del destino: te quedarás sin copia fuera del servidor hasta la próxima.";
  }
  const newest = [...files].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  if (newest && selected.has(newest.name)) return "Entre las elegidas está la copia más reciente.";
  return null;
}
